import { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import cs from "classnames";
import {
  AccordionItem,
  Accordion,
  Breadcrumbs,
  LinkGenerator,
  Select,
  Downshift
} from "@lululemon/dsp-ecom-pattern-library";
import { graphql } from "@lululemon/dsp-shared";
import gql from "graphql-tag"; // eslint-disable-line import/no-named-as-default
import { print } from "graphql/language/printer";
import { publicConfig, EVENT_CATEGORY, trackEvent } from "@lululemon/ecom-core";
import { sentryLog } from "@lululemon/dsp-shared/src/logger";
import { storeListMeta } from "./content/generateMetaData";
import {
  convertToCountryCode,
  getRegionalizedTitleTag,
  useRegionalize
} from "@lululemon/mwa-i18n";
import { useRouter } from "next/router";
import storeListContent from "./content/content";

import styles from "./store-list.module.scss";
import { useGoogleMapLoader } from "@lululemon/dsp-utils";

const storeListQuery = print(gql`
  query {
    storeLocatorList {
      city
      country
      id
      name
      slug
      state
      stateDisplayName
    }
  }
`);

export const StoreList = ({ storeListData, meta, nextLocale }) => {
  const { locale } = useRouter();
  const countryCode = convertToCountryCode(locale);
  const { regionalizeLink } = useRegionalize();

  const {
    components: { breadcrumbs, storeList }
  } = storeListContent;

  const [countrySelectValue, setCountrySelectValue] = useState(countryCode);
  const hasOneLinkTranslation = locale === "fr-ca" || null;
  /**
   * Creates dictionary of stores grouped by country and state/province.
   * example: storesRegistry["US"]["Texas"] = array of store objects in Texas, US
   */
  const storesRegistry = useMemo(() => {
    return storeListData?.reduce((storeGroups, store) => {
      const countryKey = store.country;
      const stateKey = store.stateDisplayName;

      if (!storeGroups[countryKey]) {
        // eslint-disable-next-line no-param-reassign
        storeGroups[countryKey] = { [stateKey]: [] };
      }

      if (!storeGroups[countryKey][stateKey]) {
        // eslint-disable-next-line no-param-reassign
        storeGroups[countryKey][stateKey] = [];
      }

      storeGroups[countryKey][stateKey].push(store);

      return storeGroups;
    }, {});
  }, [storeListData]);

  useEffect(() => {
    trackEvent(EVENT_CATEGORY.PAGE_VIEW, {
      headerUnified: "all-lululemon-stores",
      headerLocalized: storeList.pageHeading,
      screenType: "stores",
      category: {
        gender: "all"
      }
    });
  }, []);

  const canonicalLink = `${publicConfig.nav.apiHost}${meta.canonicalPath}`;

  const regionalizedCanonical = regionalizeLink(canonicalLink);

  const regionalizedTitleTag = getRegionalizedTitleTag(nextLocale, meta.title);

  // required to load google maps api for store locator
  const isGoogleMapLoaded = useGoogleMapLoader();

  if (!isGoogleMapLoaded) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{regionalizedTitleTag}</title>
        <link rel="canonical" href={regionalizedCanonical} />
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />

        {meta.hrefLangEntries.map((link) => (
          <link
            key={link.hreflang}
            rel={link.rel}
            href={link.href}
            hrefLang={link.hreflang}
          />
        ))}
      </Head>
      <main className={cs("lll-grid", styles.storeListContainer)}>
        <section className={styles.breadcrumbsContainer}>
          <Breadcrumbs
            seoItems={[{ url: "/stores", label: breadcrumbs.storesLabel }]}
            // eslint-disable-next-line no-unused-vars
            renderSEOToHead={(seoContent) => {}}
            ariaLabel="Breadcrumb"
          >
            <LinkGenerator href="/stores">
              {breadcrumbs.storesLabel}
            </LinkGenerator>
          </Breadcrumbs>
        </section>
        <section className={styles.storeListContent}>
          <h1 className={styles.pageHeading}>{storeList.pageHeading}</h1>
          <div className={cs("OneLinkNoTx", styles.countrySelect)}>
            <Select
              Downshift={Downshift}
              required={false}
              id="country"
              name="country"
              options={
                hasOneLinkTranslation
                  ? storeList.countrySelectOptionsFa
                  : storeList.countrySelectOptionsEn
              }
              value={countrySelectValue}
              onChange={(event) => {
                setCountrySelectValue(event.target.value);
              }}
            >
              {hasOneLinkTranslation
                ? storeList.countrySelectLabelFa
                : storeList.countrySelectLabelEn}
            </Select>
          </div>
          <Accordion
            id="store-list-accordion"
            allowMultipleExpanded
            noShadow
            className={cs("OneLinkNoTx", styles.accordionContainer)}
          >
            {storesRegistry &&
              storesRegistry[countrySelectValue] &&
              Object.keys(storesRegistry[countrySelectValue]).map((state) => (
                <AccordionItem
                  key={`store-list-${state}`}
                  className={styles.accordionItemContainer}
                  heading={
                    <span className={styles.accordionItemTitle}>{state}</span>
                  }
                  headingWrapperClassName={styles.accordionItemHeadingWrapper}
                  headingClassName={styles.accordionItemHeading}
                  panelContentClassName={styles.panelContent}
                >
                  <ul className={styles.panelContentGrid}>
                    {storesRegistry[countrySelectValue][state].map((store) => (
                      <li key={store.id} className={styles.listItem}>
                        <span className={styles.storeLinkContainer}>
                          <span
                            className={styles.cityName}
                          >{`${store.city}:`}</span>
                          <LinkGenerator
                            href={store.slug}
                            className={styles.storeLink}
                          >
                            {store.name}
                          </LinkGenerator>
                        </span>
                      </li>
                    ))}
                  </ul>
                </AccordionItem>
              ))}
          </Accordion>
        </section>
      </main>
    </>
  );
};

StoreList.getInitialProps = async ({ req, res, nextLocale }) => {
  try {
    const response = await graphql({
      query: storeListQuery,
      req,
      res,
      getSecret: req?.app?.locals?.getSecret
    });

    return {
      storeListData: response?.data?.storeLocatorList || [],
      meta: storeListMeta(),
      nextLocale
    };
  } catch (err) {
    sentryLog.error(err, {
      context: err,
      tags: {
        location: "store-list",
        action: "getInitialProps"
      }
    });
    return {
      meta: storeListMeta(),
      storeError: err.message,
      is404: true,
      nextLocale
    };
  }
};

StoreList.propTypes = {
  storeListData: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
      state: PropTypes.string,
      stateDisplayName: PropTypes.string
    })
  ),
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    canonicalPath: PropTypes.string,
    robots: PropTypes.string
  })
};

StoreList.defaultProps = {
  storeListData: [],
  meta: {}
};

export default StoreList;
